import { z } from "zod";
import { CountryCode } from "../common/country.js";
import { Locale } from "../common/localized-text.js";
import { defineRoute } from "../http/route.js";
import { ListingKind } from "./listing.js";
import { PublicProductCard } from "./public.routes.js";
/**
 * Public search (WEB-4, card 040) on Meilisearch: live products and shops that sell, by keyword in English or
 * Chinese, category, country, and filterable attributes. Hits are read back through the public views, so a result is
 * public at the moment it is served.
 */
const Public = { surface: "public", auth: "none", permission: "none", marks: [] };
const Paging = {
    limit: z.coerce.number().int().min(1).max(48).default(24),
    cursor: z.string().min(1).max(40).optional(),
};
const AttributeFilter = z
    .string()
    .regex(/^[a-z][a-z0-9_]{1,39}:.{1,100}$/, "key:value, such as material:carbon_steel");
export const PublicShopCard = z
    .object({
    slug: z.string(),
    name: z.string(),
    country: CountryCode,
    serviceRegions: z.array(CountryCode),
    logoUrl: z.url().nullable(),
})
    .meta({ id: "PublicShopCard" });
export const searchProducts = defineRoute({
    ...Public,
    id: "catalog.public.search.products",
    method: "GET",
    path: "/v1/public/search",
    summary: "Live products by keyword (typos tolerated in English, words segmented in Chinese), leaf or branch category, country (a good's origin or where a service is offered), kind, and attributes; newest first without a keyword. Facets count categories and countries",
    prd: ["WEB-4"],
    request: {
        query: z.object({
            q: z.string().trim().max(200).default(""),
            locale: Locale.default("en"),
            category: z
                .string()
                .min(1)
                .max(60)
                .optional()
                .describe("A category slug; its branch includes all below it"),
            country: CountryCode.optional(),
            kind: ListingKind.optional(),
            attr: z
                .union([AttributeFilter, z.array(AttributeFilter).max(10)])
                .optional()
                .transform((value) => (value === undefined ? [] : Array.isArray(value) ? value : [value]))
                .describe("Repeatable `key:value`; a choice by its value, a flag as true or false"),
            ...Paging,
        }),
    },
    responses: {
        200: {
            description: "Products",
            schema: z.object({
                items: z.array(PublicProductCard),
                estimatedHits: z.number().int().nonnegative().describe("How many products match, estimated"),
                facets: z.object({
                    categories: z.array(z.object({ slug: z.string(), name: z.string(), count: z.number().int() })),
                    countries: z.array(z.object({ code: CountryCode, count: z.number().int() })),
                }),
                nextCursor: z.string().nullable(),
            }),
        },
    },
    errors: ["RATE_LIMITED", "VALIDATION_FAILED"],
});
export const searchShops = defineRoute({
    ...Public,
    id: "catalog.public.search.shops",
    method: "GET",
    path: "/v1/public/search/shops",
    summary: "Shops that sell, by keyword and country",
    prd: ["WEB-4"],
    request: {
        query: z.object({
            q: z.string().trim().max(200).default(""),
            locale: Locale.default("en"),
            country: CountryCode.optional(),
            ...Paging,
        }),
    },
    responses: {
        200: {
            description: "Shops",
            schema: z.object({
                items: z.array(PublicShopCard),
                estimatedHits: z.number().int().nonnegative(),
                nextCursor: z.string().nullable(),
            }),
        },
    },
    errors: ["RATE_LIMITED", "VALIDATION_FAILED"],
});
export const searchRoutes = [searchProducts, searchShops];
//# sourceMappingURL=search.routes.js.map